/* The w1-04 eligibility engine (flight-delay-monitoring app/Intake/Eligibility), ported to
   TypeScript for the fake API only. The real verdict is always the API's; this port is held
   to the engine's own fixture table (src/test/fixtures/eligibility-cases.json) so the fake
   answers like the API would. Pure: no clock, no I/O.

   Contract 1.4 (w8-03, decisions 144–147): flight data never yields `not_now` or `ineligible`
   for a delay on its own. It decides only when it proves 3 hours or more (a gate or runway
   delay of 180 min or more); otherwise the S6 answer decides, and `delayEvidence` says whether
   the data disagrees and that the airline confirms. Q5's "data wins" and Q44's borderline
   pending are retired. */
import type { DelayEvidence, Eligibility, Money, ReasonCode, Regime } from '../contract';

type DelayBasis = DelayEvidence['basis'];

export type Disruption = 'delayed' | 'cancelled' | 'denied_boarding' | 'missed_connection';

export interface EligibilityInput {
  originCountry: string;
  destinationCountry: string;
  carrierCountry?: string | null;
  distanceKm: number;
  disruption?: Disruption | null;
  /** The figure on `arrivalBasis` (gate, runway or estimate); dropped for none / self_report. */
  arrivalDelayMin?: number | null;
  /**
   * 1.4 (w8-03): what `arrivalDelayMin` was measured from. Absent: `gate` with a figure (1.3's
   * meaning), `self_report` without.
   */
  arrivalBasis?: DelayBasis | null;
  /** 1.4: the p95 taxi-in at the final destination; a runway figure + this < 180 says "under 3 h". */
  taxiInAllowanceMin?: number | null;
  /**
   * The S6 answer. 1.4 (w8-04): `unsure_3h_or_more` is "I don't know, but I believe it was 3 hours
   * or more" (`arrivalUnsure`), counted as 3 hours or more (decision 145); on the top band it pays
   * what `EngineOptions.unsureLongHaul` says (full by default, DECISION-NEEDED).
   */
  selfReportedDelay?:
    '3h_or_more' | 'unsure_3h_or_more' | '3h_to_4h' | '4h_or_more' | 'under_3h' | 'never_arrived' | null;
  cancellationNotice?: 'less_than_14_days' | '14_days_or_more' | 'not_told' | null;
  /** Alternative to `cancellationNotice`: < 14 → less than 14 days. */
  noticeDays?: number | null;
  volunteered?: boolean | null;
  cancellationReason?: string | null;
  /** Q44 (retired by 1.4): a stored accepted review (`over_3h`) still counts a near-line gate figure as 3 h. */
  arrivalDelayReview?: 'over_3h' | 'under_3h' | null;
}

/** The engine's policy (the API's config). */
export interface EngineOptions {
  /** Where "close to the 3-hour line" starts; an accepted Q44 review counts only from here. */
  borderlineFromMin?: number;
  /** What an unsure "3 h or more" pays on the top band (DECISION-NEEDED: full). */
  unsureLongHaul?: 'full' | 'reduced';
}

const EU_MEMBER_STATES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]; // prettier-ignore
const EU_TERRITORIES_WITH_OWN_CODE = ['GP', 'GF', 'MQ', 'RE', 'YT', 'MF', 'AX'];
const EC261_AREA = new Set([...EU_MEMBER_STATES, ...EU_TERRITORIES_WITH_OWN_CODE, 'IS', 'LI', 'NO', 'CH']);
const UK = new Set(['GB']);
const UK261_CARRIERS = new Set(['GB', ...EU_MEMBER_STATES, ...EU_TERRITORIES_WITH_OWN_CODE]);
const EXTRAORDINARY = new Set([
  'weather',
  'air_traffic_control',
  'security',
  'external_strike',
  'manufacturing_defect',
]);

export const DELAY_THRESHOLD_MIN = 180;
/**
 * 1.4: where "close to the 3-hour line" starts (the API's config intake.eligibility.borderline_from_min,
 * DECISION-NEEDED 150 = the funnel's NEAR_LINE_MIN). Information only; Q44's pending band is retired.
 */
export const BORDERLINE_FROM_MIN = 150;
const LONG_HAUL_REDUCTION_MAX_MIN = 240;

export type Tier = 'short' | 'medium' | 'long';

const AMOUNTS: Record<'EC261' | 'UK261', Record<Tier, Money>> = {
  EC261: {
    short: { amount: 250, currency: 'EUR' },
    medium: { amount: 400, currency: 'EUR' },
    long: { amount: 600, currency: 'EUR' },
  },
  UK261: {
    short: { amount: 220, currency: 'GBP' },
    medium: { amount: 350, currency: 'GBP' },
    long: { amount: 520, currency: 'GBP' },
  },
};

/** Which regime governs the journey; null when it depends on an unknown carrier. */
export function regimeFor(origin: string, destination: string, carrier: string | null): Regime | null {
  if (EC261_AREA.has(origin)) return 'EC261';
  if (UK.has(origin)) return 'UK261';
  const arrivesEc = EC261_AREA.has(destination);
  const arrivesUk = UK.has(destination);
  if (!arrivesEc && !arrivesUk) return 'none';
  if (carrier === null) return null;
  if (arrivesEc && EC261_AREA.has(carrier)) return 'EC261';
  if (arrivesUk && UK261_CARRIERS.has(carrier)) return 'UK261';
  return 'none';
}

function tierFor(distanceKm: number, intra: boolean): Tier {
  if (distanceKm <= 1500) return 'short';
  if (intra || distanceKm <= 3500) return 'medium';
  return 'long';
}

function isIntra(regime: 'EC261' | 'UK261', origin: string, destination: string) {
  const area = regime === 'EC261' ? EC261_AREA : UK;
  return area.has(origin) && area.has(destination);
}

/** The Art. 7(1) band of a journey under a regime (the quote's `band`). */
export function bandFor(
  regime: 'EC261' | 'UK261',
  input: Pick<EligibilityInput, 'originCountry' | 'destinationCountry' | 'distanceKm'>,
): Tier {
  return tierFor(input.distanceKm, isIntra(regime, input.originCountry, input.destinationCountry));
}

/** Over 3,500 km, but both ends in the regime's own area: paid the middle band (Q4). */
export const intraAreaCapped = (distanceKm: number, intra: boolean) => intra && distanceKm > 3500;

/** The Art. 7(1) amount for a journey under a regime (no reduction). */
export function amountFor(
  regime: 'EC261' | 'UK261',
  input: Pick<EligibilityInput, 'originCountry' | 'destinationCountry' | 'distanceKm'>,
): Money {
  return AMOUNTS[regime][
    tierFor(input.distanceKm, isIntra(regime, input.originCountry, input.destinationCountry))
  ];
}

const result = (
  regime: Regime,
  amountPerPassenger: Money | null,
  verdict: Eligibility['verdict'],
  confidence: Eligibility['confidence'],
  reasons: ReasonCode[],
  reduced = false,
): Eligibility => ({ regime, amountPerPassenger, reduced, verdict, confidence, reasons });

const half = (m: Money): Money => ({ amount: Math.floor(m.amount / 2), currency: m.currency });

/** The arrival evidence as the engine reads it (`EligibilityInput`'s constructor in the API). */
function evidenceIn(input: EligibilityInput): { basis: DelayBasis; minutes: number | null } {
  const figure = input.arrivalDelayMin ?? null;
  let basis: DelayBasis = input.arrivalBasis ?? (figure === null ? 'self_report' : 'gate');
  const hasFigure = basis === 'gate' || basis === 'runway' || basis === 'estimate';
  if (figure === null && hasFigure) basis = 'none';
  return { basis, minutes: hasFigure ? figure : null };
}

const isMeasured = (basis: DelayBasis) => basis === 'gate' || basis === 'runway';

/** The figure the data proves when it proves 3 h or more (gate or runway); a stored accepted Q44 review counts. */
export function provenDelay(input: EligibilityInput, options: EngineOptions = {}): number | null {
  const { basis, minutes } = evidenceIn(input);
  if (minutes === null || !isMeasured(basis)) return null;
  const from = options.borderlineFromMin ?? BORDERLINE_FROM_MIN;
  if (
    minutes < DELAY_THRESHOLD_MIN &&
    basis === 'gate' &&
    input.arrivalDelayReview === 'over_3h' &&
    minutes >= from
  )
    return DELAY_THRESHOLD_MIN;
  return minutes >= DELAY_THRESHOLD_MIN ? minutes : null;
}

/** The data says under 3 h: gate < 180, or runway + taxi-in allowance < 180. An estimate or no figure can't say. */
export function dataSaysUnder(input: EligibilityInput, options: EngineOptions = {}): boolean {
  const { basis, minutes } = evidenceIn(input);
  if (minutes === null || provenDelay(input, options) !== null) return false;
  if (basis === 'gate') return minutes < DELAY_THRESHOLD_MIN;
  if (basis === 'runway') {
    const allowance = input.taxiInAllowanceMin ?? null;
    return allowance !== null && minutes + allowance < DELAY_THRESHOLD_MIN;
  }
  return false;
}

const saysThreeHoursOrMore = (r: EligibilityInput['selfReportedDelay']) =>
  r === '3h_or_more' || r === 'unsure_3h_or_more' || r === '3h_to_4h' || r === '4h_or_more';

/**
 * `EligibilityEngine::evaluate`. `forceRegime` is the fake's dev switch (the prototype's EU/UK
 * jurisdiction toggle): it replaces the scope decision for an in-scope journey.
 */
export function evaluate(
  input: EligibilityInput,
  forceRegime?: 'EC261' | 'UK261',
  options: EngineOptions = {},
): Eligibility {
  const r = withoutEvidence(input, forceRegime, options);
  const isDelay = input.disruption === 'delayed' || input.disruption === 'missed_connection';
  return { ...r, delayEvidence: isDelay ? evidence(input, r, options) : null };
}

function withoutEvidence(
  input: EligibilityInput,
  forceRegime: 'EC261' | 'UK261' | undefined,
  options: EngineOptions,
): Eligibility {
  let regime = regimeFor(input.originCountry, input.destinationCountry, input.carrierCountry ?? null);
  if (forceRegime && regime !== 'none') regime = forceRegime;
  if (regime === null) return result('none', null, 'pending', 'self_report', ['carrier_unknown']);
  if (regime === 'none') return result('none', null, 'ineligible', 'data', ['outside_scope']);

  const intra = isIntra(regime, input.originCountry, input.destinationCountry);
  const r = byDisruption(input, regime, tierFor(input.distanceKm, intra), options);
  /* Q4: a figure the intra-area cap lowered says so. */
  if (r.amountPerPassenger && intraAreaCapped(input.distanceKm, intra))
    return { ...r, reasons: [...r.reasons, 'intra_community_cap'] };
  return r;
}

/** Contract `DelayEvidence` for a delay claim's verdict. */
function evidence(input: EligibilityInput, r: Eligibility, options: EngineOptions): DelayEvidence {
  const certain = provenDelay(input, options) !== null;
  return {
    basis: evidenceIn(input).basis,
    dataCertain: certain,
    conflict: saysThreeHoursOrMore(input.selfReportedDelay ?? null) && dataSaysUnder(input, options),
    confirmWithAirline: (r.verdict === 'eligible' || r.verdict === 'pending') && !certain,
  };
}

/**
 * Q6 (`EligibilityEngine::evaluateJourney`): end to end first, then the disrupted leg alone. 1.4: a
 * leg pending only for an S6 answer while its own data says under 3 h isn't offered (the answer
 * is about the final destination, so on an earlier leg it never comes; DECISION-NEEDED).
 */
export function evaluateJourney(
  journey: EligibilityInput,
  leg: EligibilityInput | null,
  forceRegime?: 'EC261' | 'UK261',
  options: EngineOptions = {},
): { journey: Eligibility; legAlone: Eligibility | null } {
  const j = evaluate(journey, forceRegime, options);
  if (!leg || j.verdict !== 'ineligible' || !j.reasons.includes('outside_scope'))
    return { journey: j, legAlone: null };
  const alone = evaluate(leg, forceRegime, options);
  const claimable =
    alone.regime !== 'none' &&
    (alone.verdict === 'eligible' ||
      (alone.verdict === 'pending' &&
        !((leg.selfReportedDelay ?? null) === null && dataSaysUnder(leg, options))));
  return { journey: j, legAlone: claimable ? alone : null };
}

function byDisruption(
  input: EligibilityInput,
  regime: 'EC261' | 'UK261',
  tier: Tier,
  options: EngineOptions,
): Eligibility {
  const amount = AMOUNTS[regime][tier];

  switch (input.disruption ?? null) {
    case null:
      return result(regime, amount, 'pending', 'self_report', ['flight_status_pending']);
    case 'delayed':
    case 'missed_connection': {
      const reported = input.selfReportedDelay ?? null;
      const proven = provenDelay(input, options);
      /* (1) gate ≥ 180, (2) runway ≥ 180: the data decides whatever the answer; the long-haul half
         rule on the figure's own basis (DECISION-NEEDED: a runway 180–240 may be past 4 h at the gate). */
      if (proven !== null) {
        const runway: ReasonCode[] =
          evidenceIn(input).basis === 'runway' ? ['arrival_delay_runway_basis'] : [];
        if (tier === 'long' && proven <= LONG_HAUL_REDUCTION_MAX_MIN)
          return result(
            regime,
            half(amount),
            'eligible',
            'data',
            ['arrival_delay_3h_or_more', 'long_haul_delay_3_to_4h', ...runway],
            true,
          );
        return result(regime, amount, 'eligible', 'data', ['arrival_delay_3h_or_more', ...runway]);
      }
      /* (3) data says under 3 h, (4) data can't say: the answer decides and is accepted. */
      if (saysThreeHoursOrMore(reported)) {
        const unsure = reported === 'unsure_3h_or_more';
        /* Q39: on long haul "3–4 hours" pays half; an unsure answer as the policy says. */
        const reduced =
          tier === 'long' && (reported === '3h_to_4h' || (unsure && options.unsureLongHaul === 'reduced'));
        const reasons: ReasonCode[] = ['arrival_delay_3h_or_more'];
        if (reduced) reasons.push('long_haul_delay_3_to_4h');
        reasons.push(
          dataSaysUnder(input, options) ? 'reported_delay_over_data' : 'reported_delay_without_gate_data',
        );
        if (unsure) reasons.push('reported_delay_unsure');
        return result(regime, reduced ? half(amount) : amount, 'eligible', 'self_report', reasons, reduced);
      }
      switch (reported) {
        case 'never_arrived' /* (6) */:
          return result(regime, amount, 'eligible', 'self_report', ['never_arrived']);
        case 'under_3h' /* (5) their own answer, not the data, decides it */:
          return result(regime, null, 'not_now', 'self_report', ['reported_delay_under_3h']);
        default: /* no S6 answer yet: pending, whatever the data says under 3 h */
          return result(regime, amount, 'pending', 'self_report', ['flight_status_pending']);
      }
    }
    case 'cancelled': {
      const extraordinary = !!input.cancellationReason && EXTRAORDINARY.has(input.cancellationReason);
      const notice =
        input.cancellationNotice ??
        (input.noticeDays !== null && input.noticeDays !== undefined
          ? input.noticeDays < 14
            ? 'less_than_14_days'
            : '14_days_or_more'
          : null);
      if (notice === '14_days_or_more') {
        const reasons: ReasonCode[] = ['cancelled_14_days_or_more_notice'];
        if (extraordinary) reasons.push('extraordinary_circumstances_cited');
        return result(regime, null, 'not_now', 'self_report', reasons);
      }
      if (extraordinary)
        return result(regime, null, 'not_now', 'data', ['extraordinary_circumstances_cited']);
      if (notice === 'less_than_14_days')
        return result(regime, amount, 'eligible', 'self_report', ['cancelled_under_14_days_notice']);
      if (notice === 'not_told')
        return result(regime, amount, 'eligible', 'self_report', ['cancelled_without_notice']);
      return result(regime, amount, 'pending', 'self_report', ['cancellation_notice_unknown']);
    }
    case 'denied_boarding':
      if (input.volunteered === false)
        return result(regime, amount, 'eligible', 'self_report', ['denied_boarding_involuntary']);
      if (input.volunteered === true)
        return result(regime, null, 'not_now', 'self_report', ['volunteered_denied_boarding']);
      return result(regime, amount, 'pending', 'self_report', ['volunteered_unknown']);
  }
}
